import { useMemo, useState } from 'react';
import {
  ADMIN_LIST_LIMIT,
  PlatformRole,
  PlayerStatus,
  type AdminUserRow,
  type InvitationSummary
} from 'minka-shared';
import {
  Button,
  DataTable,
  Input,
  Modal,
  SearchInput,
  SectionCard,
  type DataTableColumn
} from '@/components/common';
import { useAdminIslands } from '@/hooks/useAdminIslands';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import { usePlatformInvitations } from '@/hooks/usePlatformInvitations';
import { usePresence } from '@/hooks/usePresence';
import { useTransientNotice } from '@/hooks/useTransientNotice';
import {
  FaCheckCircle,
  FaCircle,
  FaEnvelope,
  FaEnvelopeOpenText,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSyncAlt,
  FaUserShield,
  FaUserPlus
} from 'react-icons/fa';

const SELF_DEACTIVATE_TOOLTIP = 'You cannot deactivate yourself';
const LAST_SUPERADMIN_TOOLTIP = 'At least one active superadmin is required';
/** Presence is per-island by design (§6.7); the column says so where it is read. */
const PRESENCE_HINT =
  'Presence is tracked per island; users on the island list show as offline';

/** What a confirmation dialog is about to do, and how it is worded. */
interface PendingAction {
  user: AdminUserRow;
  kind: 'deactivate' | 'reactivate' | 'grant' | 'revoke';
}

const ACTION_COPY: Record<PendingAction['kind'], { title: string; body: string; confirm: string }> = {
  deactivate: {
    title: 'Deactivate account',
    body: 'They are signed out everywhere within seconds and cannot log in again. Their island roles are kept and come back if the account is reactivated.',
    confirm: 'Deactivate'
  },
  reactivate: {
    title: 'Reactivate account',
    body: 'They can log in again, and every island role they held becomes active once more.',
    confirm: 'Reactivate'
  },
  grant: {
    title: 'Grant superadmin',
    body: 'They gain the platform console: every user, every island, every organization. They still cannot enter an island they were not invited to.',
    confirm: 'Grant superadmin'
  },
  revoke: {
    title: 'Revoke superadmin',
    body: 'They lose the platform console. Their island roles are untouched.',
    confirm: 'Revoke superadmin'
  }
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

/** The status dot's colour; the console shows availability, not just presence. */
function statusColor(status: PlayerStatus): string {
  if (status === PlayerStatus.BUSY) return 'text-game-golden';
  if (status === PlayerStatus.DND) return 'text-game-orange';
  return 'text-game-teal';
}

function PlatformRoleBadge({ role }: { role: PlatformRole }) {
  if (role !== PlatformRole.SUPERADMIN) {
    return <span className="text-gray-400 text-sm">User</span>;
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold uppercase rounded-full bg-gradient-to-r from-game-orange/20 to-game-red/20 border border-game-orange/30 text-game-accent">
      <FaUserShield className="text-game-golden text-xs" />
      Superadmin
    </span>
  );
}

/** The counterpart of the error banner, for the things that went right. */
function SuccessNotice({ message }: { message: string }) {
  return (
    <div className="p-3 bg-game-medium/20 border border-game-medium/50 text-green-300 rounded-lg text-sm flex items-center gap-2">
      <FaCheckCircle className="flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

/** An error banner in the console's voice, shared by the table and the panel. */
function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="p-3 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg flex items-center gap-2">
      <FaExclamationTriangle className="text-game-golden flex-shrink-0 text-xs" />
      <span className="font-medium text-sm">{message}</span>
    </div>
  );
}

interface UsersTabProps {
  /** The signed-in superadmin, whose own row refuses to deactivate them. */
  currentUserId?: string;
  /**
   * Whether the first presence snapshot may be taken yet. False while the
   * console, entered from in-game, is still leaving the island (§6.7).
   */
  presenceReady?: boolean;
}

/**
 * The Users tab of §4.3: every account, what it can reach, and where its owner
 * is standing right now.
 *
 * Presence is one snapshot (§6.7) rather than a live feed — it is loaded with
 * the tab and on the Refresh button. Island names for it come from the islands
 * list, because the snapshot speaks in room ids: it is read from memory and
 * knows nothing about names.
 */
export function UsersTab({ currentUserId, presenceReady = true }: UsersTabProps) {
  const [search, setSearch] = useState('');
  const users = useAdminUsers(search);
  const presence = usePresence(presenceReady);
  const invitations = usePlatformInvitations();
  // Unsearched on purpose: this list is a roomId → name dictionary for the
  // presence column, not something the operator is looking at.
  const islands = useAdminIslands('');

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [emailTarget, setEmailTarget] = useState<AdminUserRow | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const islandNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const island of islands.data.rows) {
      names.set(island.id, island.name);
    }
    return names;
  }, [islands.data]);

  // The guard the server enforces (409 `last_superadmin`), shown before it is
  // hit. It reads the platform-wide count the list carries rather than the rows
  // on screen: a search that hides the other superadmins would otherwise make
  // an ordinary one look like the last (§3.1 invariant 6).
  const isLastActiveSuperadmin = (row: AdminUserRow) =>
    users.data.activeSuperadminCount === 1 &&
    row.isActive &&
    row.platformRole === PlatformRole.SUPERADMIN;

  /**
   * Every way out of a dialog, and every way into one.
   *
   * A refusal belongs to the action that provoked it: it is shown inside the
   * dialog that is asking, and it dies with it. Left alone it outlived the
   * dialog as a page banner and turned up inside the *next* one, explaining a
   * failure that had nothing to do with what was being confirmed.
   */
  const closeDialogs = () => {
    setPending(null);
    setEmailTarget(null);
    users.clearError();
  };

  const askFor = (action: PendingAction) => {
    users.clearError();
    setPending(action);
  };

  const runPending = async () => {
    if (!pending) return;

    try {
      if (pending.kind === 'deactivate') {
        await users.deactivate(pending.user.id);
      } else if (pending.kind === 'reactivate') {
        await users.reactivate(pending.user.id);
      } else {
        await users.setPlatformRole(
          pending.user.id,
          pending.kind === 'grant' ? PlatformRole.SUPERADMIN : PlatformRole.USER
        );
      }
      closeDialogs();
    } catch {
      // The hook has already put the refusal in `error`; keep the dialog open.
    }
  };

  const submitEmailChange = async () => {
    if (!emailTarget) return;

    try {
      await users.changeEmail(emailTarget.id);
      closeDialogs();
    } catch {
      // Same: the banner explains it, the dialog stays put.
    }
  };

  const closeInvite = () => {
    setIsInviting(false);
    invitations.clearError();
  };

  const submitInvite = async () => {
    try {
      await invitations.invite(inviteEmail.trim());
      closeInvite();
      setInviteEmail('');
    } catch {
      // `account_exists` and `rate_limited` are shown on the panel below.
    }
  };

  const columns: Array<DataTableColumn<AdminUserRow>> = [
    {
      key: 'username',
      header: 'User',
      render: row => (
        <div className="flex items-center gap-2">
          <span className="text-white font-medium">{row.username}</span>
          {!row.isActive && (
            <span className="px-2 py-0.5 text-xs font-semibold uppercase rounded-full bg-game-medium/20 border border-game-medium/40 text-gray-400">
              Deactivated
            </span>
          )}
        </div>
      )
    },
    { key: 'email', header: 'Email', render: row => row.email },
    {
      key: 'platformRole',
      header: 'Platform role',
      render: row => <PlatformRoleBadge role={row.platformRole} />
    },
    {
      key: 'organizations',
      header: 'Organizations',
      render: row =>
        row.organizations.length === 0 ? (
          <span className="text-gray-500">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.organizations.map(organization => (
              <span
                key={organization.id}
                className="px-2 py-0.5 text-xs rounded-full bg-game-medium/30 border border-game-medium/50 text-gray-300"
              >
                {organization.name}
              </span>
            ))}
          </div>
        )
    },
    { key: 'islands', header: 'Islands', render: row => row.islandCount },
    { key: 'lastLogin', header: 'Last login', render: row => formatDate(row.lastLogin) },
    {
      key: 'presence',
      header: (
        <span className="inline-flex items-center gap-1.5" title={PRESENCE_HINT}>
          Presence
          <FaInfoCircle className="text-gray-500 text-xs" aria-hidden="true" />
          <span className="sr-only">{PRESENCE_HINT}</span>
        </span>
      ),
      render: row => {
        const entry = presence.userPresence(row.id);
        if (!entry) {
          // Not "signed out": somebody sitting on the island list holds a live
          // session and still reads as offline here (§6.7).
          return (
            <span className="text-gray-500 cursor-help" title={PRESENCE_HINT}>
              offline
            </span>
          );
        }

        return (
          <span className="inline-flex items-center gap-1.5 text-gray-200">
            <FaCircle className={`text-[0.5rem] ${statusColor(entry.status)}`} />
            online · {islandNames.get(entry.roomId) ?? 'an island'}
          </span>
        );
      }
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: row => {
        const isSelf = row.id === currentUserId;
        const isOnlySuperadmin = isLastActiveSuperadmin(row);
        const isPending = users.pendingUserId === row.id;
        const isSuperadmin = row.platformRole === PlatformRole.SUPERADMIN;

        return (
          <div className="flex items-center justify-end gap-2">
            {row.isActive ? (
              <Button
                variant="danger"
                className="px-3 py-1 text-sm"
                disabled={isSelf || isPending}
                title={isSelf ? SELF_DEACTIVATE_TOOLTIP : 'Deactivate this account'}
                onClick={() => askFor({ user: row, kind: 'deactivate' })}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                variant="secondary"
                className="px-3 py-1 text-sm"
                disabled={isPending}
                title="Reactivate this account"
                onClick={() => askFor({ user: row, kind: 'reactivate' })}
              >
                Reactivate
              </Button>
            )}

            {/* Tombstoning an address is only ever done to a deactivated
                impostor (§6.1), so the control does not exist otherwise. */}
            {!row.isActive && (
              <Button
                variant="secondary"
                className="px-3 py-1 text-sm"
                disabled={isPending}
                title="Replace this account's email address with an undeliverable tombstone"
                onClick={() => {
                  users.clearError();
                  setEmailTarget(row);
                }}
              >
                Change email
              </Button>
            )}

            {isSuperadmin ? (
              <Button
                variant="secondary"
                className="px-3 py-1 text-sm"
                disabled={isPending || isOnlySuperadmin}
                title={isOnlySuperadmin ? LAST_SUPERADMIN_TOOLTIP : 'Revoke superadmin'}
                onClick={() => askFor({ user: row, kind: 'revoke' })}
              >
                Revoke superadmin
              </Button>
            ) : (
              // A deactivated account cannot log in, so handing it the console
              // grants nothing anyone could use; reactivate first, then grant.
              // Revoking one, on the other hand, is exactly how a deactivated
              // superadmin is tidied up, so that control stays.
              row.isActive && (
                <Button
                  variant="secondary"
                  className="px-3 py-1 text-sm"
                  disabled={isPending}
                  title="Grant superadmin"
                  onClick={() => askFor({ user: row, kind: 'grant' })}
                >
                  Grant superadmin
                </Button>
              )
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          onChange={setSearch}
          placeholder="Search by username, email or organization"
          ariaLabel="Search users"
          className="flex-1 min-w-[16rem]"
        />
        <Button
          variant="secondary"
          onClick={() => void presence.refresh()}
          isLoading={presence.isLoading}
          title="Reload the presence snapshot"
        >
          <FaSyncAlt className="text-sm" />
          Refresh presence
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            invitations.clearError();
            setIsInviting(true);
          }}
        >
          <FaUserPlus className="text-sm" />
          Invite user
        </Button>
      </div>

      {/* While a dialog is asking, its refusal is shown inside it and nowhere
          else; the banner is for what happened to the page itself. */}
      {users.error && pending === null && emailTarget === null && (
        <ErrorBanner message={users.error} />
      )}
      {presence.error && <ErrorBanner message={presence.error} />}

      {users.isLoading && users.data.rows.length === 0 ? (
        <p className="text-gray-400 italic text-center py-6">Loading users…</p>
      ) : (
        <DataTable
          columns={columns}
          rows={users.data.rows}
          getRowKey={row => row.id}
          emptyText={search ? 'No user matches that search.' : 'There are no accounts yet.'}
        />
      )}

      {users.data.truncated && (
        <p className="text-gray-400 text-sm italic">
          Showing the first {ADMIN_LIST_LIMIT} users — refine your search.
        </p>
      )}

      <PendingPlatformInvitations
        invitations={invitations.data}
        isLoading={invitations.isLoading}
        error={invitations.error}
        onRevoke={invitations.revoke}
        onResend={invitations.resend}
      />

      <Modal
        isOpen={pending !== null}
        onClose={closeDialogs}
        title={pending ? ACTION_COPY[pending.kind].title : ''}
        size="md"
      >
        {pending && (
          <div className="space-y-6">
            <p className="text-gray-300">
              <strong className="text-white">{pending.user.username}</strong> ({pending.user.email})
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">{ACTION_COPY[pending.kind].body}</p>
            {users.error && <ErrorBanner message={users.error} />}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={closeDialogs}>
                Cancel
              </Button>
              <Button
                variant={pending.kind === 'deactivate' ? 'danger' : 'primary'}
                onClick={() => void runPending()}
                isLoading={users.pendingUserId === pending.user.id}
              >
                {ACTION_COPY[pending.kind].confirm}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={emailTarget !== null}
        onClose={closeDialogs}
        title="Change email address"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-400 text-sm leading-relaxed">
            Only a deactivated account can have its address changed. Minka replaces it with a
            tombstone address that can never receive mail, which frees the real one for a fresh
            invitation. The old address is told.
          </p>
          {emailTarget && (
            <p className="text-gray-300 text-sm">
              <strong className="text-white">{emailTarget.email}</strong> →{' '}
              <code className="text-game-golden break-all">deleted+{emailTarget.id}@tombstone.invalid</code>
            </p>
          )}
          {users.error && <ErrorBanner message={users.error} />}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={closeDialogs}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submitEmailChange()}
              isLoading={users.pendingUserId === emailTarget?.id}
            >
              Change email
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isInviting}
        onClose={closeInvite}
        title="Invite a user to Minka"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-400 text-sm leading-relaxed">
            They get an account with no island. Island admins can invite them into their islands
            afterwards.
          </p>
          <Input
            label="Email address"
            type="email"
            name="inviteEmail"
            value={inviteEmail}
            onChange={event => setInviteEmail(event.target.value)}
            placeholder="someone@example.com"
          />
          {invitations.error && <ErrorBanner message={invitations.error} />}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={closeInvite}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submitInvite()}
              disabled={inviteEmail.trim() === ''}
            >
              Send invitation
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

interface PendingPlatformInvitationsProps {
  invitations: InvitationSummary[];
  isLoading: boolean;
  error: string | null;
  onRevoke: (invitationId: string) => Promise<void>;
  onResend: (invitationId: string) => Promise<void>;
}

/** The invited addresses that have not become accounts yet (§4.3). */
function PendingPlatformInvitations({
  invitations,
  isLoading,
  error,
  onRevoke,
  onResend
}: PendingPlatformInvitationsProps) {
  // A resend answers with nothing the row shows, so without this the button
  // looked broken: the operator clicked it and the screen did not move.
  const resent = useTransientNotice();

  const resend = async (invitationId: string, email: string | null) => {
    resent.clear();
    try {
      await onResend(invitationId);
    } catch {
      // The panel's own banner explains it; there is nothing to confirm.
      return;
    }
    resent.show(`Invitation re-sent to ${email ?? 'the invited address'}`);
  };

  return (
    <SectionCard title="Pending platform invitations" icon={FaEnvelopeOpenText}>
      <div className="space-y-3">
        {error && <ErrorBanner message={error} />}
        {resent.notice && <SuccessNotice message={resent.notice} />}

        {isLoading && invitations.length === 0 ? (
          <p className="text-gray-400 italic text-center py-4">Loading invitations…</p>
        ) : invitations.length === 0 ? (
          <p className="text-gray-400 italic text-center py-4">
            No platform invitation is waiting to be accepted.
          </p>
        ) : (
          <ul className="space-y-2">
            {invitations.map(invitation => (
              <li
                key={invitation.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-game-medium/20 bg-game-dark/30"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-white font-medium truncate">
                    <FaEnvelope className="text-game-golden text-xs flex-shrink-0" />
                    {invitation.email}
                  </div>
                  <div className="text-gray-400 text-xs">
                    Invited by {invitation.invitedByUsername} · expires{' '}
                    {formatDate(invitation.expiresAt)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="secondary"
                    className="px-3 py-1 text-sm"
                    onClick={() => void resend(invitation.id, invitation.email)}
                    title="Send the invitation again with a fresh link"
                  >
                    Resend
                  </Button>
                  <Button
                    variant="danger"
                    className="px-3 py-1 text-sm"
                    onClick={() => void onRevoke(invitation.id)}
                    title="Kill this invitation link"
                  >
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}
